import { ref } from 'vue';
import { Description } from '@/types/watchface';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { emitter, EventType } from '@/utils/emitter.utils';
import { useLayerStore } from '@/store/use-layer-store';
import { useImagesStore } from '@/store/use-images-store';
import { WatchfaceService } from '@/services/watchface';
import { buildAst } from '@/utils/ast';
import { astToXml } from '@/utils/xmlTool';
import { useTranslationStore } from '@/store/use-translation-store';
import { GlobalConfig } from '@/services/gloabl.config';
import {
  watchfaceImageLoading,
  watchfaceOpening,
  useImageAutoFormat,
  useRouteState,
  useWatchfaceRam
} from '.';
import { useFormatsStore } from '@/store/use-formats-store';
import { writeCapabilitySet } from '@/utils/capabilitySet';
import { getEditorVersion, getWebBuildTime } from '@/utils/version.utils';
import { useDataSourceStore } from '@/store/use-data-source-store';
import { generateM66WatchfaceId } from '@/utils/watchface-id-m66.utils';
import { ElectronApi } from '@/services/electron.api';
import { Modal } from 'ant-design-vue';

/**
 * 表盘正在保存状态
 */
export const watchfaceSaving = ref(false);

export const useWatchfaceSave = () => {
  const layerStore = useLayerStore();
  const imageStore = useImagesStore();
  const translationStore = useTranslationStore();
  const watchfaceStore = useWatchfaceStore();
  const formatsStore = useFormatsStore();
  const dataSourceStore = useDataSourceStore();

  const { resetImgsFormat } = useImageAutoFormat();
  const { isEditorPage } = useRouteState();
  const { imageArrayRamMethod } = useWatchfaceRam();

  const _saveImages = async (watchfaceService: WatchfaceService) => {
    // 添加空图片，
    imageStore.addEmptyImage();
    // 添加默认背景
    const addBgImage = GlobalConfig.needToAddDefaultBgImageDevices.includes(
      watchfaceStore.deviceType
    );
    if (addBgImage) {
      imageStore.addBgImage({
        ...watchfaceStore.size
      });
    }
    // 保存图片资源
    const images = imageStore.imgs.map((item) => {
      return {
        base64: item.value ?? '',
        basename: item.basename
      };
    });
    await watchfaceService.resource.writeImages(images);
  };

  const _saveEditorJsonConfig = async (watchfaceService: WatchfaceService) => {
    const themes = layerStore.themes;
    const i18n = {
      translations: translationStore.translations,
      locales: translationStore.locales
    };
    const formats = GlobalConfig.autoChooseImageFormat
      ? { ...formatsStore.formats }
      : {};
    const dataSource = dataSourceStore.data;
    const isSlotFollowing = watchfaceStore.slotFollowing;
    await watchfaceService.editorConfig.writeEditorData({
      themes,
      aodDisplayMode: layerStore.aodDisplayMode,
      i18n,
      formats,
      dataSource,
      isSlotFollowing
    });
  };

  const editorDataToAst = async (options?: {
    exportForApp?: boolean;
    keepUslessAttr?: boolean;
    buildFor?: 'editor' | 'export';
  }) => {
    const addBgImage = GlobalConfig.needToAddDefaultBgImageDevices.includes(
      watchfaceStore.deviceType
    );
    // 保存manifest
    const ast = buildAst({
      keepUslessAttr: options?.keepUslessAttr ? true : false,
      buildFor: options?.buildFor ?? 'export',
      addDefaultBgImage: addBgImage,
      exportForApp: !!options?.exportForApp
    });
    return ast;
  };

  const _saveManifest = async (
    watchfaceService: WatchfaceService,
    buildFor: 'editor' | 'export'
  ) => {
    const ast = await editorDataToAst({ buildFor });
    if (ast) await watchfaceService.manifest.writeManifestXML(ast);
    return ast;
  };

  const _resetM66AtuoId = (watchfaceAst: Ast) => {
    const pkgName = generateM66WatchfaceId(watchfaceAst);
    if (pkgName) {
      watchfaceStore.description.pkgName = pkgName;
      watchfaceAst.attrsMap.id = pkgName;
    }
  };

  const _saveDescription = async (
    watchfaceService: WatchfaceService,
    ast: Ast | undefined
  ) => {
    const desc = {
      ...watchfaceStore.description,
      imageArrayRamMethod: imageArrayRamMethod.value,
      watchOS:
        watchfaceStore.description.watchOS == 'android' ? 'android' : 'vela'
    } as Description;
    desc._recolorEnable = watchfaceStore.colorMode == 'recolor' ? true : false;
    if (desc._recolorEnable) {
      desc.recolorTable = ast?.attrsMap?.['recolorTable'] ?? '';
    } else {
      delete desc.recolorTable;
    }
    desc.webVersionExportAt = getWebBuildTime();
    desc.editorVersionExportAt = await getEditorVersion();
    await watchfaceService.description.writeDecsriptionXML(desc);
    return desc;
  };

  // 保存预览图：根据新规则进行保存
  const _savePreviews = async (service: WatchfaceService) => {
    const { path, fs } = ElectronApi;
    if (!path || !fs) return;

    // 将theme预览图保存到preview文件夹
    // 获取所有的themes预览图
    const resourcePath = service.ResourcePath;
    // 将图片复制到preview文件夹
    const previewDir = service.PreviewPath;

    const normalThemes = layerStore.themes.filter(
      (item) => item.type == 'normal'
    );
    const aodThemes = layerStore.themes.filter((item) => item.type == 'AOD');

    const _previewFiles: string[] = [];

    const _handleStaticImages = async () => {
      const cpImageItems: {
        from: string;
        to: string;
      }[] = [];
      // 获取需要设置的预览图
      const firstAodTheme = layerStore.themes.find(
        (item) => item.type == 'AOD'
      );
      for (let i = 0; i < normalThemes.length; i++) {
        const normalTheme = normalThemes[i];
        // 对应的aod
        const aodTheme = layerStore.anotherTheme(normalTheme) ?? firstAodTheme;
        ([normalTheme, aodTheme] as StoreThemeItem[])
          // 去除不存在的theme
          .filter((item) => !!item)
          .forEach((theme) => {
            const preview = theme?.preview;
            if (!preview) return;

            const _to =
              theme.type == 'normal'
                ? `style_${i + 1}_static.png`
                : `style_${i + 1}_aod.png`;

            _previewFiles.push(_to);
            cpImageItems.push({
              from: path.join(resourcePath, preview),
              to: path.join(previewDir, _to)
            });
          });
      }

      for (const item of cpImageItems) {
        const { from, to } = item;

        await fs.promises.cp(from, to, {
          force: true
        });
      }
    };

    const _handleAniImages = async () => {
      // 处理亮屏动效预览图
      for (let i = 0; i < normalThemes.length; i++) {
        const theme = normalThemes[i];
        const previewAni = theme.previewAni;
        if (!previewAni) continue;
        // 将previewAni对应的文件名重命名
        const previewAniNew =
          `style_${i + 1}_animated` + path.extname(previewAni);
        _previewFiles.push(previewAniNew);

        if (previewAni == previewAniNew) continue;

        const _previewAni = path.join(previewDir, previewAni);
        const _previewAniNew = path.join(previewDir, previewAniNew);
        await fs.promises.cp(_previewAni, _previewAniNew, {
          force: true
        });
        // 修改theme上的previewAni属性
        theme.previewAni = previewAniNew;
        // 删除旧文件
        await fs.promises.rm(_previewAni);
      }

      // 处理AOD动效预览图
      for (let i = 0; i < aodThemes.length; i++) {
        const theme = aodThemes[i];
        const previewAni = theme.previewAni;
        if (!previewAni) continue;
        const previewAniNew =
          `style_${i + 1}_aod_animated` + path.extname(previewAni);
        _previewFiles.push(previewAniNew);

        if (previewAni == previewAniNew) continue;

        const _previewAni = path.join(previewDir, previewAni);
        const _previewAniNew = path.join(previewDir, previewAniNew);
        await fs.promises.cp(_previewAni, _previewAniNew, {
          force: true
        });
        theme.previewAni = previewAniNew;
        await fs.promises.rm(_previewAni);
      }
    };

    // 处理静态预览图
    await _handleStaticImages();
    // 处理动态预览图
    await _handleAniImages();

    // 删除多余的样式静图和动图
    // 删除preview文件夹下的所有以style_开头的png文件
    const previewFiles = await fs.promises.readdir(previewDir);
    for (const previewFile of previewFiles) {
      // 排查非style_开头的文件
      if (!previewFile.startsWith('style_')) continue;
      // 判断是否存在该index的theme
      if (!_previewFiles.includes(previewFile)) {
        // 删除之
        const filename = path.join(previewDir, previewFile);
        await fs.promises.rm(filename);
      }
    }
  };

  /**
   * 保存表盘数据
   */
  const saveWatchfaceData = async (saveFor: 'export' | 'normal' = 'normal') => {
    try {
      if (!isEditorPage.value) return;
      if (watchfaceOpening.value || watchfaceImageLoading.value) return;
      if (watchfaceSaving.value) return console.warn('忙碌，请稍后...');
      watchfaceSaving.value = true;
      console.time('saveWatchfaceData');
      const watchfaceService = new WatchfaceService();

      /**
       * 保存图片时会清空图片数据
       * 所有rotatedImages需在之后进行
       */
      await _saveImages(watchfaceService);

      if (saveFor == 'export' && GlobalConfig.autoChooseImageFormat) {
        // 自动判断图片格式-需要在保存资源之后执行
        await resetImgsFormat();
      }

      // 保存theme预览图到preview文件夹
      // 需要在_saveEditorJsonConfig之前执行
      if (saveFor == 'export') {
        await _savePreviews(watchfaceService);
      }

      // 保存themes
      await _saveEditorJsonConfig(watchfaceService);

      // 保存manifest
      const ast =
        saveFor == 'export'
          ? await _saveManifest(watchfaceService, 'export')
          : // 在点击保存按钮且为开发环境时，保存与导出一致的manifest（包含动效Slot等结构）
          import.meta.env.DEV
          ? await _saveManifest(watchfaceService, 'export')
          : undefined;

      // 如果有ast，则重置M66表盘ID
      if (ast && watchfaceStore.deviceType == 'M66') {
        _resetM66AtuoId(ast);
      }

      // 保存description
      const desc = await _saveDescription(watchfaceService, ast);

      if (saveFor == 'export' && ast) {
        // 保存表盘能力集
        await writeCapabilitySet(desc, ast, watchfaceStore.path);
      }

      console.timeEnd('saveWatchfaceData');
      emitter.emit(EventType['watchface:saved']);
    } catch (error) {
      console.error('saveWatchfaceData', error);
      if (saveFor == 'export') {
        Modal.error({
          title: '保存失败',
          content: '数据保存失败，无法正常导出',
          zIndex: 9999
        });
        // 重新抛出错误，让调用方知道保存失败，停止导出流程
        throw error;
      }
    } finally {
      watchfaceSaving.value = false;
    }
  };

  const delUslessResources = async (watchfaceAst: Ast) => {
    // 最后清空未用到的图片资源
    // 如果是导出时候的保存，需要将旧资源删除
    const watchfaceService = new WatchfaceService();
    await watchfaceService.resource.deleteUseleseFiles(
      JSON.stringify(layerStore.themes),
      astToXml(watchfaceAst)
    );
    // 删除空目录
    await watchfaceService.resource.deleteEmptyDirs();
  };

  return {
    saveWatchfaceData,
    editorDataToAst,
    delUslessResources,
    watchfaceSaving
  };
};
